import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faClose } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  LinkBox,
  LinkOther,
  LinkOverlay,
} from '@sorare/core/src/atoms/navigation/Box';
import { BrandXXS, HeadlineM } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { ANY_SPORT_MARKET_WATCHLISTS_ID } from '@sorare/core/src/constants/__generated__/routes';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { glossary } from '@sorare/core/src/lib/glossary';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import {
  desktopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import type { SorareWatchlistBanner_watchlist } from './__generated__/index.graphql.ts';
import bundesligaBackground from './assets/bundesliga.webp';
import challengerBackground from './assets/challenger.webp';
import contenderBackground from './assets/contender.webp';
import defaultBackground from './assets/default.webp';
import erdivisieBackground from './assets/eredivisie.webp';
import j1LeagueBackground from './assets/j1-league.webp';
import jupilerBackground from './assets/jupiler.webp';
import kLeague1Background from './assets/k-league.webp';
import laligaBackground from './assets/laliga.webp';
import ligue1Background from './assets/ligue-1.webp';
import mlsBackground from './assets/mls.webp';
import premierLeagueBackground from './assets/premier-league.webp';

enum Backgrounds {
  CHALLENGER = 'CHALLENGER',
  CONTENDER = 'CONTENDER',
  DEFAULT = 'DEFAULT',
  JUPILER = 'JUPILER',
  EREDIVISIE = 'EREDIVISIE',
  PREMIER_LEAGUE = 'PREMIER_LEAGUE',
  LIGUE_1 = 'LIGUE_1',
  LALIGA = 'LALIGA',
  BUNDESLIGA = 'BUNDESLIGA',
  J1_LEAGUE = 'J1_LEAGUE',
  MLS = 'MLS',
  K_LEAGUE_1 = 'K_LEAGUE_1',
}

const backgrounds: Record<Backgrounds, string> = {
  [Backgrounds.CHALLENGER]: challengerBackground,
  [Backgrounds.CONTENDER]: contenderBackground,
  [Backgrounds.DEFAULT]: defaultBackground,
  [Backgrounds.JUPILER]: jupilerBackground,
  [Backgrounds.EREDIVISIE]: erdivisieBackground,
  [Backgrounds.PREMIER_LEAGUE]: premierLeagueBackground,
  [Backgrounds.LIGUE_1]: ligue1Background,
  [Backgrounds.LALIGA]: laligaBackground,
  [Backgrounds.BUNDESLIGA]: bundesligaBackground,
  [Backgrounds.J1_LEAGUE]: j1LeagueBackground,
  [Backgrounds.MLS]: mlsBackground,
  [Backgrounds.K_LEAGUE_1]: kLeague1Background,
};

const Root = styled(Vertical).attrs({ gap: 2 })`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-50);
  padding: var(--double-unit);
  @media ${tabletAndAbove} {
    background-position: center;
    height: calc(11 * var(--unit));
    padding: var(--triple-unit);
    flex-direction: row;
    justify-content: space-between;
  }
`;

const Left = styled(Vertical)`
  align-items: flex-start;
  z-index: 0;
  @media ${tabletAndAbove} {
    flex-direction: row;
    align-items: center;
    gap: var(--double-unit);
  }
`;

const StyledChip = styled(Chip)`
  flex-shrink: 0;
`;

const RightCorner = styled(Horizontal)`
  position: absolute;
  right: var(--unit);
  top: var(--unit);
`;

const Players = styled(Horizontal).attrs({ gap: 1 })`
  position: absolute;
  transform: translateX(-50%);
  top: calc(6 * var(--unit));
  bottom: 0;
  align-items: flex-start;
  left: 80%;
  @media ${tabletAndAbove} {
    top: var(--double-unit);
    left: 58%;
    display: flex;
  }
`;

const Player = styled.img`
  width: calc(8 * var(--unit));

  @media ${tabletAndAbove} {
    width: calc(10 * var(--unit));
  }
  &:not(:first-child) {
    margin-left: calc(-5 * var(--unit));
    @media ${desktopAndAbove} {
      margin-left: calc(-2 * var(--unit));
    }
  }
`;

type Props = {
  sorareWatchlists: Nullable<SorareWatchlistBanner_watchlist[]>;
  withContainer?: boolean;
};

export const SorareWatchlistBanner = ({
  sorareWatchlists,
  withContainer,
}: Props) => {
  const {
    flags: { useSorareWatchlistBackground = Backgrounds.DEFAULT },
  } = useFeatureFlags();

  const background = backgrounds[useSorareWatchlistBackground as Backgrounds];

  const { up: tabletOrAbove } = useScreenSize('tablet');

  const { lifecycle, update: updateLifecycle } = useLifecycle();

  if (!sorareWatchlists || sorareWatchlists.length === 0) return null;
  const watchlist = sorareWatchlists[0];
  const { sport, playersPanel } = watchlist;

  const showcasedPlayers = [...playersPanel, ...playersPanel]?.slice(
    0,
    tabletOrAbove ? 5 : 3
  );

  if (
    lifecycle?.lastDismissedSorareWatchlists?.[sport] === sorareWatchlists[0].id
  ) {
    return null;
  }

  const closeButton = (
    <IconButton
      size="small"
      color="transparent"
      onClick={() => {
        updateLifecycle(LIFECYCLE.lastDismissedSorareWatchlists, {
          ...lifecycle?.lastDismissedSorareWatchlists,
          [sport]: watchlist.id,
        });
      }}
    >
      <FontAwesomeIcon icon={faClose} size="lg" />
    </IconButton>
  );
  return (
    <ConditionalWrapper wrap={!!withContainer} Wrapper={FullWidthContainer}>
      <LinkBox as="div" className="dark-mode">
        <Root
          style={{
            background: `no-repeat right center / cover url('${background}')`,
          }}
        >
          <Players>
            {showcasedPlayers?.map(cp => (
              <Player key={cp.id} src={cp.anyPlayer.fullPictureUrl} />
            ))}
          </Players>
          {!tabletOrAbove && <RightCorner>{closeButton}</RightCorner>}
          <Left>
            <StyledChip color="brand" size="small">
              <FormattedMessage {...glossary.new} />
            </StyledChip>
            <Vertical>
              <BrandXXS>
                <FormattedMessage
                  id="watchlist.SorareWatchlistBanner.curatedBySorare"
                  defaultMessage="Curated by Sorare"
                />
              </BrandXXS>
              <HeadlineM>{watchlist.title}</HeadlineM>
            </Vertical>
          </Left>
          <Horizontal>
            <LinkOverlay
              as={Link}
              to={generateSportPath(ANY_SPORT_MARKET_WATCHLISTS_ID, {
                sport,
                params: {
                  id: idFromObject(watchlist.id),
                },
              })}
            >
              <Button asSpan color="secondary" size="small">
                <FormattedMessage {...glossary.discover} />
              </Button>
            </LinkOverlay>
            {!!tabletOrAbove && <LinkOther as="div">{closeButton}</LinkOther>}
          </Horizontal>
        </Root>
      </LinkBox>
    </ConditionalWrapper>
  );
};

SorareWatchlistBanner.fragments = {
  watchlist: gql`
    fragment SorareWatchlistBanner_watchlist on Watchlist {
      slug
      id
      title
      sport
      playersPanel {
        id
        anyPlayer {
          slug
          fullPictureUrl
        }
      }
    }
  ` as TypedDocumentNode<SorareWatchlistBanner_watchlist>,
};
